import {
  Box,
  BoxProps,
  Checkbox,
  Divider,
  Flex,
  FlexProps,
  FormLabel,
  Image,
  ImageProps,
  Slider,
  SliderFilledTrack,
  SliderProps,
  SliderThumb,
  SliderTrack,
} from "@chakra-ui/react";
import React, { ReactNode } from "react";

import { SettingsIcon } from "@nens/ranalib";
import { DISABLED_OPACITY } from "@nens/ranalib";

import ColorPaletteIcon from "../icons/colorpalette_icon.svg?react";

const LayerSubMenu = ({ children }: { children: ReactNode }) => {
  return (
    <Flex flexDir="column" flexGrow={1} overflowY="hidden">
      {children}
    </Flex>
  );
};

export const LayerSubMenuOpenButton = ({
  onClick,
  active,
}: {
  onClick: () => void;
  active: boolean;
}) => {
  return (
    <Flex
      cursor="pointer"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <SettingsIcon rounded="standard" bg={active ? "brand.glacier" : undefined} boxSize="24px" />
    </Flex>
  );
};

LayerSubMenu.OpenSubMenuButton = LayerSubMenuOpenButton;

const Header = ({ children }: { children: ReactNode }) => {
  return (
    <>
      <Divider />
      <FormLabel mb={2} fontSize="16px" mx={4}>
        {children}
      </FormLabel>
    </>
  );
};

LayerSubMenu.Header = Header;

const ItemList = ({ children, ...rest }: { children: ReactNode } & FlexProps) => {
  return (
    <Flex gap={1} flexDir="column" fontSize="16px" lineHeight={6} mb={4} {...rest}>
      {children}
    </Flex>
  );
};

LayerSubMenu.ItemList = ItemList;

type Props = {
  subMenu?: ReactNode;
  children?: ReactNode;
  title?: string;
  active?: boolean;
  onClick?: () => void;
  indent?: boolean;
  disabledMessage?: string;
} & FlexProps;

const Item = ({
  children,
  subMenu,
  title,
  active,
  indent,
  onClick,
  disabledMessage,
  ...rest
}: Props) => {
  return (
    <Flex
      alignItems="center"
      ml={indent ? 8 : 4}
      mr={4}
      bg="white"
      rounded="standard"
      border="1px solid"
      borderColor="brand.border"
      position="relative"
      {...(disabledMessage
        ? { title: disabledMessage, opacity: DISABLED_OPACITY }
        : {
            title,
            onClick,
            _hover: onClick ? { cursor: "pointer", bg: "button.tertiary.hover" } : {},
          })}
      {...rest}
    >
      {onClick ? (
        <Checkbox
          pointerEvents="none"
          isDisabled={!!disabledMessage}
          w="fit-content"
          px={4}
          py={2}
          isChecked={active}
        />
      ) : (
        <Box pl={4} />
      )}

      <Flex
        pr={4}
        py={2}
        pointerEvents="all"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexGrow={1}
      >
        {children}
      </Flex>
    </Flex>
  );
};

const ItemName = ({ children, ...rest }: { children: ReactNode } & BoxProps) => {
  return (
    <Box
      width="fit-content"
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      {...rest}
    >
      {children}
    </Box>
  );
};
Item.Name = ItemName;

const ItemColor = ({
  onClick,
  value,
  isNode,
  isDashed,
}: {
  onClick: (e: React.MouseEvent<Element, MouseEvent>) => void;
  value: string;
  isNode?: boolean;
  isDashed?: boolean;
}) => {
  return (
    <Flex alignItems="center" gap={1} ml={4} onClick={onClick}>
      <ColorPaletteIcon height="16px" width="16px" />
      <svg height="14" viewBox="-14 -14 28 28">
        {isNode ? (
          <circle
            strokeDasharray={isDashed ? "10,5" : undefined}
            stroke={value}
            fill="#FFFFFF"
            strokeWidth="2px"
            r="12"
          />
        ) : (
          <line
            strokeDasharray={isDashed ? "10,5" : undefined}
            x1="28"
            y1="28"
            x2="-14"
            y2="-14"
            stroke={value}
            strokeWidth="10"
          />
        )}
      </svg>
    </Flex>
  );
};
Item.Color = ItemColor;

const ItemSlider = (props: SliderProps) => {
  return (
    <Slider w="50%" aria-label="slider-ex-1" {...props}>
      <SliderTrack>
        <SliderFilledTrack />
      </SliderTrack>
      <SliderThumb />
    </Slider>
  );
};
Item.Slider = ItemSlider;

const ItemLegend = ({ children }: { children: ReactNode }) => {
  return (
    <Flex w="50%" ml="auto" alignItems="center" display="flex" justifyContent="flex-end">
      {children}
    </Flex>
  );
};

const LegendGradient = ({ children, ...rest }: { children: ReactNode } & BoxProps) => {
  return (
    <Box width="full" h={4} {...rest}>
      {children}
    </Box>
  );
};

const GradientImage = (props: ImageProps) => {
  return <Image h="full" w="full" {...props} />;
};

LegendGradient.Image = GradientImage;
ItemLegend.Gradient = LegendGradient;
Item.Legend = ItemLegend;

LayerSubMenu.Item = Item;

export default LayerSubMenu;
